import React, { useState, useEffect } from 'react';
import { FaQrcode, FaDownload, FaCopy, FaKey, FaCheck, FaInfoCircle, FaApple, FaGooglePlay, FaMobileAlt } from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import { useQRCode } from '../hooks/useQRCode';
import { showToast } from '../utils/toast';

const InvitePage = () => {
  const { currentUser, brand, brandName, brandLogo, branding } = useProContext();
  const [copiedCode, setCopiedCode] = useState(false);
  const { qrCode, loading: qrLoading, fetchQRCode, downloadQRCode } = useQRCode(currentUser?.id);

  // Single source of truth: the branding state from context (sourced from API at login)
  const isWhiteLabel = !!(branding?.slug && branding.slug !== 'fideliz');

  const [activeTab, setActiveTab] = useState(isWhiteLabel ? 'download' : 'code');

  useEffect(() => {
    if (activeTab === 'qr') fetchQRCode();
  }, [activeTab, fetchQRCode]);

  const handleCopyCode = () => {
    if (currentUser?.store_code) {
      navigator.clipboard.writeText(currentUser.store_code);
      setCopiedCode(true);
      showToast.success('Code copié !');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // Tabs vary based on white-label mode
  const tabs = isWhiteLabel
    ? [
        { key: 'download', icon: FaMobileAlt, label: 'Télécharger l\'app' },
      ]
    : [
        { key: 'code', icon: FaKey, label: "Code d'invitation" },
        { key: 'qr', icon: FaQrcode, label: 'QR Code' },
      ];

  return (
    <ProLayout title="Inviter un utilisateur">
      <Toaster />
      <div className="p-6 max-w-4xl mx-auto">
        {/* Intro */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-6">
          <FaInfoCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            {isWhiteLabel ? (
              <>
                <p className="text-[0.8125rem] font-semibold text-blue-900">Votre application est personnalisée</p>
                <p className="text-[0.6875rem] text-blue-700 mt-0.5 leading-relaxed">
                  Vos clients téléchargent directement <strong>{brandName}</strong> sur l'App Store ou Google Play. Ils sont automatiquement rattachés à votre boutique dès l'inscription, sans code ni QR code à saisir.
                </p>
              </>
            ) : (
              <>
                <p className="text-[0.8125rem] font-semibold text-blue-900">Deux façons d'inviter un utilisateur</p>
                <p className="text-[0.6875rem] text-blue-700 mt-0.5 leading-relaxed">
                  Partagez le code de votre boutique ou imprimez le QR code à afficher en magasin.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Tabs (only shown when not white-label, or always hidden if single tab) */}
        {tabs.length > 1 && (
          <div className="flex border-b border-gray-200 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.key ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {activeTab === tab.key && <div className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{ backgroundColor: brand }} />}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {activeTab === 'code' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-3">Code de votre boutique</p>
              <div className="flex items-center gap-4">
                <div
                  className="flex-1 text-center py-6 rounded-xl border-2 border-dashed"
                  style={{ borderColor: brand + '40', backgroundColor: brand + '08' }}
                >
                  <p className="text-4xl font-bold tracking-widest" style={{ color: brand }}>
                    {currentUser?.store_code || 'N/A'}
                  </p>
                </div>
                <button
                  onClick={handleCopyCode}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${copiedCode ? 'bg-green-500 text-white' : 'text-white hover:opacity-90'}`}
                  style={!copiedCode ? { backgroundColor: brand } : {}}
                >
                  {copiedCode ? <><FaCheck className="w-3.5 h-3.5" /> Copié</> : <><FaCopy className="w-3.5 h-3.5" /> Copier</>}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-3">Instructions pour votre client</p>
              <div className="space-y-3">
                {[
                  'Télécharger l\'application sur son téléphone',
                  'Créer un compte avec son email et numéro',
                  `Entrer le code boutique : ${currentUser?.store_code}`,
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0" style={{ backgroundColor: brand }}>
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'qr' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            {qrLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-t-transparent" style={{ borderColor: brand, borderTopColor: 'transparent' }} />
              </div>
            ) : qrCode ? (
              <div className="text-center">
                <div className="inline-block p-4 bg-white rounded-2xl border border-gray-100 mb-5">
                  <img src={qrCode.image_url} alt="QR Code" className="w-56 h-56" />
                </div>
                <p className="text-sm font-semibold text-gray-900">QR Code — {currentUser?.optician_name}</p>
                <p className="text-[0.75rem] text-gray-400 mt-1 mb-5">À scanner pour s'inscrire directement à votre boutique</p>
                <button
                  onClick={downloadQRCode}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  <FaDownload className="w-3.5 h-3.5" /> Télécharger le QR Code
                </button>
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-sm text-gray-400">Aucun QR Code disponible</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'download' && (
          <div className="space-y-5">
            {/* Brand card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
              {brandLogo && (
                <img src={brandLogo} alt={brandName} className="h-14 mx-auto object-contain mb-4" />
              )}
              <h3 className="text-lg font-bold text-gray-900">{brandName}</h3>
              <p className="text-sm text-gray-500 mt-1">Application mobile</p>
              <div className="flex items-center justify-center gap-3 mt-5">
                <button
                  className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-xl hover:opacity-90"
                  onClick={() => showToast.success('Lien App Store bientôt disponible')}
                >
                  <FaApple className="w-4 h-4" />
                  App Store
                </button>
                <button
                  className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-xl hover:opacity-90"
                  onClick={() => showToast.success('Lien Google Play bientôt disponible')}
                >
                  <FaGooglePlay className="w-4 h-4" />
                  Google Play
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-3">Comment ça marche</p>
              <div className="space-y-3">
                {[
                  { title: 'Le client trouve l\'application', desc: `Il cherche ${brandName} dans l'App Store ou Google Play` },
                  { title: 'Il télécharge l\'application', desc: `${brandName} s'installe sur son téléphone` },
                  { title: 'Il crée son compte', desc: 'Il est automatiquement rattaché à votre boutique, sans code à saisir' },
                  { title: 'Il bénéficie de vos avantages', desc: 'Carte de fidélité, recommandations, SAV...' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: brand }}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                      <p className="text-[0.6875rem] text-gray-500 mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default InvitePage;
